// DRV-5a (#2883) — compact, read-only Drive connection status for the
// Settings → Storage page's "Google Drive authorisation" card. Reads GET
// /tenant-integrations/me on mount; management (Reconnect, Disconnect) lives
// on /settings/integrations/gdrive behind the "Manage" link.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { driveDisplayState, fetchDriveIntegration, type DriveIntegration } from '../../lib/driveIntegration';

type Load = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; integration: DriveIntegration };

export default function DriveStatusSummary() {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    fetchDriveIntegration()
      .then((integration) => { if (active) setLoad({ kind: 'ready', integration }); })
      .catch(() => { if (active) setLoad({ kind: 'error' }); });
    return () => { active = false; };
  }, []);

  let status: React.ReactNode;
  let state: string;
  if (load.kind === 'loading') {
    state = 'loading';
    status = 'Checking the Drive connection…';
  } else if (load.kind === 'error') {
    state = 'error';
    status = 'Couldn’t load the Drive connection status.';
  } else {
    state = driveDisplayState(load.integration);
    const email = load.integration.metadata?.account?.emailAddress;
    status = {
      connected:         <>Connected as <strong data-testid="drive-status-email">{email}</strong></>,
      connected_unknown: 'Connected (account not recorded; reconnect to record it)',
      revoked:           'Access revoked or expired. Reconnect to restore it.',
      admin_policy:      'Blocked by your Google Workspace administrator.',
      not_connected:     'Not connected',
    }[state as ReturnType<typeof driveDisplayState>];
  }

  return (
    <div data-testid="drive-status-summary" data-state={state}>
      <p data-testid="drive-status-text" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', marginBottom: 12 }}>
        {status}
      </p>
      <Link
        to="/settings/integrations/gdrive"
        style={{
          display:        'inline-block',
          padding:        '8px 16px',
          fontFamily:     'var(--font-display)',
          fontSize:       13,
          fontWeight:     600,
          color:          'white',
          background:     'var(--primary)',
          border:         'none',
          borderRadius:   'var(--radius-sm)',
          textDecoration: 'none',
        }}
      >
        Manage Google Drive →
      </Link>
    </div>
  );
}
